import { useMemo, useEffect } from "react";

/**
 * useSummaryFilteredProjects — extract dari ProjectsPage.jsx (149-ai/aj cleanup).
 *
 * Terima raw `displayedProjects` (yang sudah di-sort + hierarki-preserving), plus
 * current filter state (summary/status/preset/search), return `filteredProjects`
 * yang siap render + `filteredIds` set untuk render-loop orphan detection.
 *
 * Juga auto-expand parent yang punya child aktif via setChildrenOpen callback.
 *
 * Kontrak yang dilindungi:
 *  - Force-inject parent kalau child match tapi parent di-exclude predikat (149-ai)
 *  - Preserve hierarki order via displayedProjects order index
 *  - Emit list flat: parent → matching kids → orphan children
 */
export function useSummaryFilteredProjects({
  displayedProjects,
  summaryFilter,
  statusFilter,
  searchQ,
  presetProjectIds,
  setChildrenOpen,
}) {
  const filteredProjects = useMemo(() => {
    const q = (searchQ || "").trim().toLowerCase();
    const tokens = q ? q.split(/\s+/).filter(Boolean) : [];
    const searchable = (p) => [
      p.project_name, p.client, p.city, p.province,
      p.wo_number, p.wo_title, p.variation_order_name, p.po_number, p.contract_number,
      p.parent_project_name, p.outlet, p.outlet_name, p.store_name, p.notes,
    ].filter(Boolean).join(" ").toLowerCase();
    const matchesSearch = (p) => tokens.length === 0 || tokens.every((t) => searchable(p).includes(t));

    const parentPredicate = (p) => {
      if (["variation_order", "working_order"].includes(p.project_type)) return false;
      if (summaryFilter === "combined") return p.project_type === "new_project" && p.vo_aggregation === "combined";
      if (summaryFilter === "retention_pending") return (p.income_entries || []).some((e) => e.is_retention_claim);
      if (summaryFilter === "over_contract") {
        // Sumber kanonis backend (Asia/Jakarta) — frontend tidak menghitung ulang.
        return (p.schedule?.overdue_days || 0) > 0;
      }
      return true;
    };
    const statusPredicate = (p) => {
      if (statusFilter === "all") return true;
      const s = p.status || "active";
      if (statusFilter === "active") return s === "active" || s === "awaiting_payment";
      if (statusFilter === "completed") return s === "completed";
      if (statusFilter === "cancelled") return s === "cancelled";
      return true;
    };
    const presetSet = presetProjectIds ? new Set(presetProjectIds) : null;
    const presetPredicate = (p) => {
      if (!presetSet) return true;
      if (presetSet.has(p.id)) return true;
      if (p.parent_project_id && presetSet.has(p.parent_project_id)) return true;
      return false;
    };

    const projectById = new Map(displayedProjects.map((p) => [p.id, p]));
    const allParents = displayedProjects.filter((p) => parentPredicate(p) && statusPredicate(p) && presetPredicate(p));
    const allChildren = displayedProjects.filter((p) => ["variation_order", "working_order"].includes(p.project_type) && presetPredicate(p));
    const parentIds = new Set(allParents.map((p) => p.id));

    // Force-inject parent (149-ai)
    const injectedParents = [];
    const injectedParentIds = new Set();
    const walkUpChain = (childProj) => {
      const chain = [];
      let cur = childProj;
      const guard = new Set();
      while (cur && cur.parent_project_id && !guard.has(cur.parent_project_id)) {
        guard.add(cur.parent_project_id);
        const par = projectById.get(cur.parent_project_id);
        if (!par) break;
        chain.push(par);
        cur = par;
      }
      return chain;
    };
    for (const c of allChildren) {
      if (!c.parent_project_id) continue;
      const ancestors = walkUpChain(c);
      for (const anc of ancestors) {
        if (parentIds.has(anc.id) || injectedParentIds.has(anc.id)) continue;
        if (!presetPredicate(anc)) continue;
        injectedParents.push(anc);
        injectedParentIds.add(anc.id);
      }
    }
    const orderIdx = new Map(displayedProjects.map((p, i) => [p.id, i]));
    const mergedParents = [...allParents, ...injectedParents];
    mergedParents.sort((a, b) => (orderIdx.get(a.id) ?? 0) - (orderIdx.get(b.id) ?? 0));
    const mergedParentIds = new Set(mergedParents.map((p) => p.id));

    const out = [];
    const seenIds = new Set();
    for (const parent of mergedParents) {
      const isInjected = injectedParentIds.has(parent.id);
      const parentMatches = matchesSearch(parent);
      const kids = allChildren.filter((c) => c.parent_project_id === parent.id);
      const matchingKids = kids.filter(matchesSearch);
      if (parentMatches && !isInjected) {
        out.push(parent); seenIds.add(parent.id);
        for (const k of kids) { out.push(k); seenIds.add(k.id); }
      } else if (matchingKids.length > 0) {
        out.push(parent); seenIds.add(parent.id);
        for (const k of matchingKids) { out.push(k); seenIds.add(k.id); }
      }
    }
    for (const c of allChildren) {
      if (seenIds.has(c.id)) continue;
      if (c.parent_project_id && mergedParentIds.has(c.parent_project_id)) continue;
      if (!matchesSearch(c)) continue;
      out.push(c); seenIds.add(c.id);
    }
    return out;
  }, [displayedProjects, summaryFilter, searchQ, statusFilter, presetProjectIds]);

  // Auto-expand children under matching filter (149-ai)
  useEffect(() => {
    const filtersActive = !!(searchQ && searchQ.trim()) || statusFilter !== "all" || summaryFilter !== "all" || !!presetProjectIds;
    if (!filtersActive) return;
    const parentIdsToOpen = new Set();
    for (const p of filteredProjects) {
      if (["variation_order", "working_order"].includes(p.project_type) && p.parent_project_id) {
        parentIdsToOpen.add(p.parent_project_id);
      }
    }
    if (parentIdsToOpen.size === 0) return;
    setChildrenOpen((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const id of parentIdsToOpen) {
        if (!next[id]) { next[id] = true; changed = true; }
      }
      return changed ? next : prev;
    });
  }, [filteredProjects, searchQ, statusFilter, summaryFilter, presetProjectIds, setChildrenOpen]);

  const filteredIds = useMemo(() => new Set(filteredProjects.map((p) => p.id)), [filteredProjects]);

  return { filteredProjects, filteredIds };
}
